import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronRight, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getSupabaseClient } from "@/lib/supabase/client";

const GOLD = "#d4af37";

const FALLBACK_FEATURES = [
  "Tüm meditasyon ve nefes içerikleri",
  "Detaylı numeroloji ve kader haritası",
  "Seni beğenenleri gör",
  "Eşleşme olmadan mesaj gönder",
  "Sınırsız çember katılımı",
  "Karmik döngü analizi",
];

type Plan = {
  id: string;
  monthly_price: number;
  yearly_price: number;
  yearly_savings_percent: number;
  features?: string[];
};

async function fetchPlan(): Promise<Plan> {
  const res = await fetch("/api/admin/subscription-plans/public");
  const json = await res.json();
  if (!res.ok || !json.plan) throw new Error(json.error ?? "Plan bulunamadı.");
  return json.plan as Plan;
}

async function createPremiumOrder(input: {
  plan: "monthly" | "yearly";
  userId: string;
  firstName: string;
  lastName: string;
  phone: string;
  identityNumber: string;
  address: string;
  district: string;
  city: string;
  zipCode: string;
}): Promise<{ ok: boolean; orderId: string; token: string }> {
  const res = await fetch("/api/numeroloji/premium-purchase", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const json = await res.json();
  if (!res.ok || !json.ok) throw new Error(json.error ?? "Sipariş oluşturulamadı.");
  return json;
}

export default function NumeralojiPremium() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const planParam = searchParams.get("plan") === "monthly" ? "monthly" : "yearly";

  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "yearly">(planParam as "monthly" | "yearly");
  const [userId, setUserId] = useState<string | null>(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    getSupabaseClient().auth.getUser().then(({ data }) => {
      setUserId(data?.user?.id ?? null);
      setUserLoading(false);
    });
  }, []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [identityNumber, setIdentityNumber] = useState("");
  const [address, setAddress] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");

  const { data: plan } = useQuery({ queryKey: ["subscription-plan"], queryFn: fetchPlan });

  const features = plan?.features?.length ? plan.features : FALLBACK_FEATURES;
  const monthlyPrice = plan?.monthly_price ?? 199;
  const yearlyPrice = plan?.yearly_price ?? 1499;
  const savings = plan ? Math.round((1 - (plan.yearly_price / 12) / plan.monthly_price) * 100) : 37;

  const purchaseMutation = useMutation({
    mutationFn: createPremiumOrder,
    onSuccess: (data) => {
      navigate(`/numeroloji/odeme?orderId=${encodeURIComponent(data.orderId)}&token=${encodeURIComponent(data.token)}`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    console.log('[Premium] submit userId:', userId, 'plan:', selectedPlan);
    if (!userId) return;
    purchaseMutation.mutate({ plan: selectedPlan, userId, firstName, lastName, phone, identityNumber, address, district, city, zipCode });
  }

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.12) 0%, transparent 55%), radial-gradient(circle at 80% 20%, rgba(103,75,181,0.10) 0%, transparent 50%)",
        }}
      />

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-4">
            <Link to="/numeroloji" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <span style={{ color: GOLD }}>Premium</span>
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Soul Circles Premium
          </h1>
          <p className="mt-2 text-slate-400">Ruhsal yolculuğunu derinleştir</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Plan selection + features */}
          <div className="flex flex-col gap-6">
            {/* Plan cards */}
            <div className="grid grid-cols-2 gap-4">
              {/* Yearly */}
              <button
                type="button"
                onClick={() => setSelectedPlan("yearly")}
                className="relative rounded-2xl border p-5 text-left transition-all"
                style={{
                  borderColor: selectedPlan === "yearly" ? GOLD : "rgba(255,255,255,0.10)",
                  background: selectedPlan === "yearly" ? "rgba(212,175,55,0.07)" : "rgba(255,255,255,0.03)",
                  boxShadow: selectedPlan === "yearly" ? `0 0 16px rgba(212,175,55,0.15)` : "none",
                }}
              >
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-bold"
                  style={{ background: GOLD, color: "#241a00" }}
                >
                  %{savings} tasarruf
                </span>
                <div className="text-[10px] font-bold tracking-widest text-slate-400 mt-2 mb-2">YILLIK</div>
                <div className="font-display text-2xl font-bold text-white">₺{yearlyPrice}</div>
                <div className="text-xs text-slate-500">/yıl</div>
                <div className="text-xs text-slate-500 mt-1">≈ ₺{Math.round(yearlyPrice / 12)}/ay</div>
              </button>

              {/* Monthly */}
              <button
                type="button"
                onClick={() => setSelectedPlan("monthly")}
                className="rounded-2xl border p-5 text-left transition-all"
                style={{
                  borderColor: selectedPlan === "monthly" ? GOLD : "rgba(255,255,255,0.10)",
                  background: selectedPlan === "monthly" ? "rgba(212,175,55,0.07)" : "rgba(255,255,255,0.03)",
                  boxShadow: selectedPlan === "monthly" ? `0 0 16px rgba(212,175,55,0.15)` : "none",
                }}
              >
                <div className="text-[10px] font-bold tracking-widest text-slate-400 mb-2">AYLIK</div>
                <div className="font-display text-2xl font-bold text-white">₺{monthlyPrice}</div>
                <div className="text-xs text-slate-500">/ay</div>
              </button>
            </div>

            {/* Features */}
            <div
              className="rounded-2xl border p-6"
              style={{ borderColor: "rgba(212,175,55,0.12)", background: "rgba(0,0,0,0.25)" }}
            >
              <div className="text-sm font-semibold text-white mb-4">Premium'a dahil</div>
              <ul className="flex flex-col gap-3">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />
                    <span className="text-sm text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 shrink-0" />
              Ödeme iyzico güvenli ödeme altyapısıyla işlenir.
            </div>
          </div>

          {/* Order form */}
          <div
            className="rounded-3xl border p-6 sm:p-8"
            style={{
              borderColor: "rgba(212,175,55,0.16)",
              background: "radial-gradient(circle at 60% 0%, rgba(212,175,55,0.08) 0%, transparent 50%), linear-gradient(145deg, rgba(26,18,37,0.60), rgba(10,10,12,0.75))",
            }}
          >
            <div className="mb-6">
              <div className="text-lg font-semibold text-white">Ödeme Bilgileri</div>
              <div className="mt-1 text-sm text-slate-400">
                {selectedPlan === "yearly"
                  ? `Yıllık plan — ₺${yearlyPrice}`
                  : `Aylık plan — ₺${monthlyPrice}`}
              </div>
            </div>

            {!userLoading && !userId && (
              <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
                Lütfen önce Soul Circles uygulamasından giriş yapın.
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="firstName" className="text-xs font-bold uppercase tracking-widest text-slate-400">Ad</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Adınız"
                    required
                    className="border-white/10 bg-white/5 text-white placeholder:text-slate-600"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="lastName" className="text-xs font-bold uppercase tracking-widest text-slate-400">Soyad</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Soyadınız"
                    required
                    className="border-white/10 bg-white/5 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest text-slate-400">Telefon</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+90 5xx xxx xx xx"
                  required
                  className="border-white/10 bg-white/5 text-white placeholder:text-slate-600"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="address" className="text-xs font-bold uppercase tracking-widest text-slate-400">Adres</Label>
                <Textarea
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Fatura adresi"
                  required
                  rows={3}
                  className="border-white/10 bg-white/5 text-white placeholder:text-slate-600 resize-none"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="district" className="text-xs font-bold uppercase tracking-widest text-slate-400">İlçe</Label>
                  <Input
                    id="district"
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    placeholder="İlçe"
                    required
                    className="border-white/10 bg-white/5 text-white placeholder:text-slate-600"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="city" className="text-xs font-bold uppercase tracking-widest text-slate-400">Şehir</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Şehir"
                    required
                    className="border-white/10 bg-white/5 text-white placeholder:text-slate-600"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="zipCode" className="text-xs font-bold uppercase tracking-widest text-slate-400">Posta Kodu</Label>
                  <Input
                    id="zipCode"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="34000"
                    className="border-white/10 bg-white/5 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="identityNumber" className="text-xs font-bold uppercase tracking-widest text-slate-400">TC Kimlik No</Label>
                <Input
                  id="identityNumber"
                  value={identityNumber}
                  onChange={(e) => setIdentityNumber(e.target.value)}
                  placeholder="11 haneli TC No"
                  maxLength={11}
                  pattern="[0-9]{11}"
                  required
                  className="border-white/10 bg-white/5 text-white placeholder:text-slate-600"
                />
              </div>

              {purchaseMutation.isError ? (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                  {(purchaseMutation.error as Error).message}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={purchaseMutation.isPending || userLoading || !userId}
                className="mt-2 h-12 rounded-xl font-bold"
                style={{ background: `linear-gradient(90deg, ${GOLD}, #af8d11)`, color: "#241a00" }}
              >
                {userLoading ? "Yükleniyor..." : purchaseMutation.isPending ? "İşleniyor..." : `₺${selectedPlan === "yearly" ? yearlyPrice : monthlyPrice} — Ödemeye Geç`}
              </Button>

              <p className="text-center text-xs text-slate-500">
                Devam ederek <span className="text-slate-400">Hizmet Şartları</span>'nı kabul etmiş olursunuz.
              </p>
            </form>
          </div>
        </div>
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.08)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-4">
          <div className="h-20 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-2xl tracking-widest" style={{ color: GOLD }}>Numeroloji.app</div>
            <p className="mt-2 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
